import React, { useEffect, useRef, useContext, useCallback } from 'react'

import { dataIdFromObject } from '@sketch/graphql-cache'
import {
  useForDesktop,
  handleFetchMore,
  LoadPageSeparator,
} from '@sketch/components'
import { noop } from '@sketch/utils'

import CommentNewSeparator from '../CommentNewSeparator'
import AnnotationsSkeleton from '../AnnotationsSkeleton'
import CommentSkeleton from '../CommentSkeleton'
import AnnotationCommentBox from '../AnnotationCommentBox'
import { CommentEditingProvider } from '../CommentEditContext'
import CommentOptions from '../CommentOptions'
import AnnotationPopoverResolutionFooter from '../AnnotationPopoverResolutionFooter'

import { DraftCommentContext } from '../DraftCommentContext'
import useGetAnnotationComments from '../../operations/useGetAnnotationComments'
import useCreateAnnotationComment from '../../operations/useCreateAnnotationComment'
import useMarkCommentAsRead from '../../operations/useMarkCommentAsRead'
import useMarkCommentsAsReadList, {
  createCommentProps,
} from '../../hooks/useMarkCommentsAsReadList'

import { isCommentNew, isAnnotationCommentRead } from '../../utils'
import { useActiveAnnotation } from '../../hooks'

import {
  AnnotationComment,
  AnnotationCommentsWrapper,
  AnnotationFirstCommentSeparator,
} from './AnnotationCommentsList.styles'

export type CommentType = 'FLOATING' | 'FIXED'

interface AnnotationCommentsListProps {
  annotationIdentifier: string
  isViewingLatestVersion: boolean
  header?: React.ReactNode
  cacheOnly?: boolean
  commentType: CommentType
  /**
   * URL of the latest version of the document. Forwarded to
   * `AnnotationCommentBox` to render the "you're viewing an older version"
   * warning banner. Required when `isViewingLatestVersion` is false; ignored
   * otherwise.
   */
  latestVersionUrl?: string
}

const ENTRIES_PATH = ['annotation', 'comments', 'entries']
const LOADING_PAGE_SIZE = 6

/**
 * __AnnotationCommentsList__
 *
 * This component displays an incrementally-loaded list of annotation comments.
 *
 * These can be rendered in one of two ways: in a popover as part of a
 * *floating* annotation, or as *fixed* annotations in the sidebar. Use the
 * `commentType` prop to choose how comments should be rendered.
 */
const AnnotationCommentsList = (props: AnnotationCommentsListProps) => {
  const {
    annotationIdentifier,
    header,
    cacheOnly,
    commentType,
    isViewingLatestVersion,
    latestVersionUrl,
  } = props
  const listRef = useRef<HTMLDivElement>(null)
  const firstAnnotationCommentRef = useRef<HTMLDivElement | null>(null)

  const isDesktopAndBigger = useForDesktop()
  const shouldAutoFocusInput = isDesktopAndBigger

  const { loading, data, error, fetchMore } = useGetAnnotationComments(
    { annotationIdentifier },
    cacheOnly
  )

  const { getDraftComment, setDraftComment } = useContext(DraftCommentContext)

  const draftComment = getDraftComment(annotationIdentifier)

  const createComment = useCreateAnnotationComment(annotationIdentifier)
  const observe = useMarkCommentsAsReadList()
  const [activeAnnotation] = useActiveAnnotation()

  const commentIdentifierSeparator = useRef<string | null>(null)

  const scrollToComment = useCallback(() => {
    const firstAnnotationCommentElement = firstAnnotationCommentRef.current

    if (!firstAnnotationCommentElement) return

    // We check if the first annotation comment is visible
    const observer = new IntersectionObserver(
      (entries, observer) => {
        if (entries[0].intersectionRatio <= 0) {
          // If not visible, we can scroll
          listRef.current?.scrollTo({
            top: 0,
          })
        }

        observer.disconnect()
      },
      {
        threshold: 0,
      }
    )

    firstAnnotationCommentElement &&
      observer.observe(firstAnnotationCommentElement)
  }, [])

  useEffect(() => {
    /**
     *  Reset the "commentIdentifierSeparator" if the "annotationIdentifier" changes
     *  meaning it will be a different list
     */
    commentIdentifierSeparator.current = null
  }, [annotationIdentifier])

  /**
   * Mark the first comment as read, if it isn't,
   * since the first comment influences the "annotation.isRead"
   */
  const markCommentAsRead = useMarkCommentAsRead()
  useEffect(() => {
    if (data?.annotation?.isNew) {
      markCommentAsRead([data.annotation.firstComment.identifier])
    }
  }, [data?.annotation, markCommentAsRead])

  /**
   * Mark replies as read if this is the active annotation when this component unmounts
   */

  const cachedComments = useRef(data?.annotation?.comments.entries || [])
  const isActiveAnnotation = activeAnnotation === data?.annotation?.identifier
  cachedComments.current = data?.annotation?.comments.entries || []

  useEffect(() => {
    return () => {
      if (isActiveAnnotation) {
        const comments = cachedComments.current
        const unreadComments = comments?.filter(comment => !comment.isRead)

        const unreadCommentIdentifiers =
          unreadComments?.map(comment => comment.identifier) || []

        markCommentAsRead(unreadCommentIdentifiers)
      }
    }
  }, [isActiveAnnotation, markCommentAsRead])

  const after = data?.annotation?.comments.meta.after

  if (loading || error || !data) {
    return (
      <>
        {header}
        <AnnotationsSkeleton
          error={error}
          placeholderCount={commentType === 'FLOATING' ? 1 : undefined}
        />
        {!error && (
          <AnnotationCommentBox
            shareIdentifier=""
            onCommentSubmit={noop}
            showOlderVersionWarning={false}
          />
        )}
      </>
    )
  }

  const {
    comments,
    share,
    firstComment: firstAnnotationComment,
    resolution,
  } = data.annotation!

  const { entries, meta } = comments
  const [firstComment] = entries
  const hasReplies = meta.totalCount > 1

  /** Scroll all the way to the top if the first annotation comment is no read */
  if (
    !commentIdentifierSeparator.current && // There is no "Read" reply comments (all new and unread)
    !isAnnotationCommentRead(firstAnnotationComment) // First annotation comment is not read
  ) {
    window.requestAnimationFrame(scrollToComment)
  }

  /* Render the comments list */
  const renderedComments = comments.entries.map(comment => {
    /**
     * Save the comment identifier on the ref
     * so when the comments is marked as read we don't
     * loose the reference to it.
     */
    if (
      !commentIdentifierSeparator.current &&
      isCommentNew(firstComment) &&
      !isCommentNew(comment)
    ) {
      // The ID saved here is the comment ID just before the first unread comment
      // - Read
      // - Read -> This ID
      // - (NEW)
      // - Unread
      // - Unread
      commentIdentifierSeparator.current = comment.identifier
    }

    const isFirstComment =
      firstAnnotationComment.identifier === comment.identifier

    const showOptions =
      (comment.userIsCreator || comment.userCanDelete) &&
      !(commentType === 'FLOATING' && isFirstComment)

    return (
      <React.Fragment key={comment.identifier}>
        <AnnotationComment
          ref={ref => {
            if (ref) {
              // Add an Observe to each comment item so we can mark them as read when they are visible
              observe(ref)

              // Save the first annotation comment element in a ref so we can scroll to it if needed
              if (isFirstComment) {
                firstAnnotationCommentRef.current = ref
              }
            }
          }}
          data-first-comment={isFirstComment}
          showPressedState={commentType !== 'FLOATING'}
          body={comment.body}
          user={comment.user}
          identifier={comment.identifier}
          shareIdentifier={share.identifier}
          createdAt={comment.createdAt!}
          edited={comment.isEdited}
          optionsMenu={className =>
            showOptions ? (
              <CommentOptions
                className={className}
                annotationIdentifier={annotationIdentifier}
                commentIdentifier={comment.identifier}
                isFirstComment={isFirstComment}
                hasReplies={hasReplies}
                isAnnotationResolved={!!resolution}
              />
            ) : null
          }
          {...createCommentProps(comment.identifier, !comment.isRead)}
        />

        {/* Render the annotation list if it hasn't been rendered already */}
        {commentIdentifierSeparator.current === comment.identifier &&
          hasReplies && (
            <CommentNewSeparator shouldScrollOnMount={!listRef.current} />
          )}

        {isFirstComment && hasReplies && <AnnotationFirstCommentSeparator />}
      </React.Fragment>
    )
  })

  const handleCommentSubmit = (body: string) => {
    setDraftComment({
      parentAnnotationId: annotationIdentifier,
      commentValue: '',
    })

    // Create the comment
    createComment({
      variables: {
        identifier: annotationIdentifier,
        body,
      },
    })

    // Scroll to the end
    setTimeout(() => {
      listRef.current?.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }, 0)
  }

  return (
    <CommentEditingProvider>
      <AnnotationCommentsWrapper ref={listRef}>
        {/**
         * The header is included inside the list rendering
         * so we take advance of position sticky and show a
         * border bellow the header when there's more items
         * to load
         */}
        {header}

        {/**
         * The CommentSkeleton renders the annotation placeholders
         */}
        <CommentSkeleton
          count={Math.min(LOADING_PAGE_SIZE, meta.totalCount - entries.length)}
        />

        {after && (
          <LoadPageSeparator
            key={after}
            loadNewPage={handleFetchMore(fetchMore, ENTRIES_PATH, {
              dataIdFromObject,
              after,
            })}
          />
        )}

        {[...renderedComments].reverse()}
      </AnnotationCommentsWrapper>
      {resolution ? (
        <AnnotationPopoverResolutionFooter
          user={resolution.user}
          resolvedDate={resolution.resolvedAt!}
        />
      ) : (
        <AnnotationCommentBox
          shareIdentifier={share.identifier}
          showOlderVersionWarning={!isViewingLatestVersion}
          latestVersionUrl={latestVersionUrl}
          comment={draftComment?.commentValue}
          onCommentChange={(commentValue: string) => {
            setDraftComment({
              parentAnnotationId: annotationIdentifier,
              commentValue,
            })
          }}
          onCommentSubmit={handleCommentSubmit}
          isReply
          autoFocus={shouldAutoFocusInput}
        />
      )}
    </CommentEditingProvider>
  )
}

export default AnnotationCommentsList
